import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { message, Modal, Input } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Search } from "lucide-react";
import {
  Current_Designation,
  Add_Update_Designation,
  Get_Designation_Users_Count,
  List_Gender_From_Role,
  Add_Update_Gender_Master,
  Get_Gender_Users_Count,
  List_Employement_Type_From_Role,
  Add_Update_Employement_Type,
  Get_Employment_Type_Users_Count,
} from "../../services/userAPI";
import BackArrow from "../../Images/backArrowLiteAndGrowthImage.png";
import DeleteIcon from "../../Images/deleteIcon.png";
import RoleAttendanceSection from "./RoleAttendanceSection";
import RoleInputIndicators from "./RoleInputIndicators";
import MasterConfigIcon from "../../Images/liteandgrowthdashboardaMasterConfigurationIcon.png";
import MasterConfigGreyIcon from "../../Images/liteandgrowthdashboardaMasterConfigurationGreyIcon.png";
import InputIndicatorsIcon from "../../Images/liteandgrowthdashboardaInputIndicatorsIcon.png";
import InputIndicatorsGreyIcon from "../../Images/liteandgrowthdashboardaInputIndicatorsgreyIcon.png";
import UserManagementIcon from "../../Images/liteandgrowthdashboardaUserManagementIcon.png";
import UserManagementGreyIcon from "../../Images/liteandgrowthdashboardaUserManagementGreyIcon.png";
import RoleThresholds from "./RoleThresholds";

/* --------------------------- Config section card --------------------------- */

const ConfigSection = ({
  title,
  addLabel,
  searchPlaceholder,
  searchTerm,
  onSearch,
  onAdd,
  items,
  onDelete,
  loading,
}) => (
  <div
    className="bg-white rounded-[12px] p-4 border border-[#F0F0F0] flex flex-col gap-[25px]"
    style={{ boxShadow: "0 2px 8px -2px rgba(0,0,0,0.06)" }}
  >
    <h3 className="text-[18px] font-semibold leading-none tracking-[-0.36px] text-[#383838]">{title}</h3>
    <div className="flex items-center gap-5">
      <div className="flex-1 flex items-center h-12 rounded-[6px] border border-[#D9DDE3] px-4 bg-white">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="flex-1 h-full bg-transparent outline-none text-[16px] tracking-[-0.16px] text-[#1F2020] placeholder:text-[#A19A9A]"
        />
        <Search className="h-4 w-4 text-[#737373] shrink-0" />
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="h-12 px-5 rounded-[6px] bg-[#AD0101] hover:bg-[#8C0101] text-white text-[14px] font-medium whitespace-nowrap transition"
      >
        {addLabel}
      </button>
    </div>

    <div>
      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="h-6 w-6 border-2 border-[#AD0101] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="py-6 text-[13px] text-[#888888] text-center">No {title.toLowerCase()} found</p>
      ) : (
        items.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between gap-[76px] min-h-[58px] pl-3 border-b border-[#BABABA] last:border-b-0"
          >
            <span className="text-[14px] text-[#1F2020]">{item.label}</span>
            <button
              type="button"
              onClick={() => onDelete(item)}
              className="h-8 w-8 flex items-center justify-center rounded hover:bg-[#FDECEA] transition"
            >
              <img src={DeleteIcon} alt="Delete" className="h-[18px] w-[18px] object-contain" />
            </button>
          </div>
        ))
      )}
    </div>
  </div>
);


/* --------------------------------- Page ------------------------------------ */

const RoleMasterConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const roleId = location.state?.roleId;
  const accountId = location.state?.accountId;
  const roleName = location.state?.roleName || "";

  /* ------------------------------ Designations ----------------------------- */
  const { data: desigData, loading: desigLoading, refetch: refetchDesig } = useQuery(
    Current_Designation,
    {
      variables: { jobroleId: roleId, accountId },
      skip: !roleId || !accountId,
      fetchPolicy: "network-only",
    }
  );
  const [addUpdateDesignation] = useMutation(Add_Update_Designation);
  const [checkDesignationCount] = useLazyQuery(Get_Designation_Users_Count, {
    fetchPolicy: "network-only",
  });

  /* --------------------------------- Gender --------------------------------- */
  const { data: genderData, loading: genderLoading, refetch: refetchGender } = useQuery(
    List_Gender_From_Role,
    { variables: { jobroleId: roleId }, skip: !roleId, fetchPolicy: "network-only" }
  );
  const [addUpdateGender] = useMutation(Add_Update_Gender_Master);
  const [checkGenderCount] = useLazyQuery(Get_Gender_Users_Count, { fetchPolicy: "network-only" });

  /* ----------------------------- Employment Type ---------------------------- */
  const { data: empData, loading: empLoading, refetch: refetchEmp } = useQuery(
    List_Employement_Type_From_Role,
    { variables: { jobRoleId: roleId }, skip: !roleId, fetchPolicy: "network-only" }
  );
  const [addUpdateEmployementType] = useMutation(Add_Update_Employement_Type);
  const [checkEmploymentTypeCount] = useLazyQuery(Get_Employment_Type_Users_Count, {
    fetchPolicy: "network-only",
  });

  /* ------------------------------ local state ------------------------------ */
  const [desigSearch, setDesigSearch] = useState("");
  const [genderSearch, setGenderSearch] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [modal, setModal] = useState({ open: false, section: null, value: "" });
  const [saving, setSaving] = useState(false);
  const [mainTab, setMainTab] = useState("role-config");

  const designations = (desigData?.getDesignationsJobroleid || [])
    .map((d) => ({ id: d.id, label: d.designation, name: d.designation }))
    .filter((d) => d.label?.toLowerCase().includes(desigSearch.toLowerCase()));

  const genders = (genderData?.listGenderFromRole || [])
    .map((g) => ({ id: g.id, label: g.name, name: g.name, code: g.code }))
    .filter((g) => g.label?.toLowerCase().includes(genderSearch.toLowerCase()));

  const employments = (empData?.listEmployementTypeFromRole || [])
    .map((e) => ({ id: e.id, label: e.name, name: e.name }))
    .filter((e) => e.label?.toLowerCase().includes(empSearch.toLowerCase()));

  /* ---------------------------------- add ---------------------------------- */
  const openModal = (section) => setModal({ open: true, section, value: "" });
  const closeModal = () => setModal({ open: false, section: null, value: "" });

  const handleAdd = async () => {
    const value = modal.value.trim();
    if (!value) {
      message.error("Please enter a name");
      return;
    }
    setSaving(true);
    try {
      let res;
      if (modal.section === "designation") {
        res = await addUpdateDesignation({
          variables: {
            accountId: Number(accountId),
            jobroleId: Number(roleId),
            designationName: value,
            designationId: null,
          },
        });
        const ok = res?.data?.addUpdateDesignations;
        if (!ok?.success) return message.error(ok?.message || "Failed to add designation");
        refetchDesig();
      } else if (modal.section === "gender") {
        res = await addUpdateGender({
          variables: { genderName: value, genderCode: "", jobRoleId: roleId, id: null },
        });
        const ok = res?.data?.addUpdateGenderMaster;
        if (!ok?.success) return message.error(ok?.message || "Failed to add gender");
        refetchGender();
      } else {
        res = await addUpdateEmployementType({
          variables: { employementType: value, jobRoleId: roleId },
        });
        const ok = res?.data?.addUpdateEmployementType;
        if (!ok?.success) return message.error(ok?.message || "Failed to add employment type");
        refetchEmp();
      }
      message.success("Added successfully");
      closeModal();
    } catch (err) {
      message.error(err?.graphQLErrors?.[0]?.message || err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------------- delete --------------------------------- */
  const [deleteTarget, setDeleteTarget] = useState(null); // { item, section }
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = (item, section) => setDeleteTarget({ item, section });

  const runDelete = async () => {
    if (!deleteTarget) return;
    const { item, section } = deleteTarget;
    setDeleting(true);
        try {
          if (section === "designation") {
            const countRes = await checkDesignationCount({
              variables: { accountId, jobroleId: roleId, designationId: item.id },
            });
            const count = countRes?.data?.getDesignationUsersCount ?? 0;
            if (count > 0) {
              return message.error(`Cannot delete: ${count} user(s) are assigned to this designation.`);
            }
            const res = await addUpdateDesignation({
              variables: {
                accountId: Number(accountId),
                jobroleId: Number(roleId),
                designationName: item.name,
                designationId: item.id,
                status: 0,
              },
            });
            const ok = res?.data?.addUpdateDesignations;
            if (!ok?.success) return message.error(ok?.message || "Failed to delete designation");
            refetchDesig();
          } else if (section === "gender") {
            const countRes = await checkGenderCount({
              variables: { accountId: Number(accountId), jobroleId: Number(roleId), genderId: item.id },
            });
            const count = countRes?.data?.getGenderUsersCount ?? 0;
            if (count > 0) {
              return message.error(`Cannot delete: ${count} user(s) are assigned to this gender.`);
            }
            const res = await addUpdateGender({
              variables: {
                genderId: item.id,
                genderName: item.name,
                genderCode: item.code || "",
                jobRoleId: roleId,
                status: 0,
              },
            });
            const ok = res?.data?.addUpdateGenderMaster;
            if (!ok?.success) return message.error(ok?.message || "Failed to delete gender");
            refetchGender();
          } else {
            const countRes = await checkEmploymentTypeCount({
              variables: {
                accountId: Number(accountId),
                jobroleId: Number(roleId),
                employmentTypeId: item.id,
              },
            });
            const count = countRes?.data?.getEmploymentTypeUsersCount ?? 0;
            if (count > 0) {
              return message.error(
                `Cannot delete: ${count} user(s) are assigned to this employment type.`
              );
            }
            const res = await addUpdateEmployementType({
              variables: {
                employmentTypeId: item.id,
                employementType: item.name,
                jobRoleId: roleId,
                status: 0,
              },
            });
            const ok = res?.data?.addUpdateEmployementType;
            if (!ok?.success) return message.error(ok?.message || "Failed to delete employment type");
            refetchEmp();
          }
          message.success("Deleted successfully");
        } catch (err) {
          message.error(err?.graphQLErrors?.[0]?.message || err.message || "Something went wrong");
        } finally {
          setDeleting(false);
          setDeleteTarget(null);
        }
  };

  const [activeNav, setActiveNav] = useState("master");
  const sideNavItems = [
    {
      key: "master",
      label: "Master Configuration",
      icon: MasterConfigIcon,
      iconGrey: MasterConfigGreyIcon,
    },
    {
      key: "indicators",
      label: "Input Indicators",
      icon: InputIndicatorsIcon,
      iconGrey: InputIndicatorsGreyIcon,
    },
    {
      key: "users",
      label: "User Management",
      icon: UserManagementIcon,
      iconGrey: UserManagementGreyIcon,
    },
  ];

  return (
    <div className="min-h-screen px-6 pt-[88px] pb-10">
      <div className="flex gap-6 items-stretch">
        {/* Left nav rail */}
        <aside
          className="w-[232px] shrink-0 bg-white rounded-[12px] pt-5 px-4 pb-4 flex flex-col gap-5"
          style={{
            boxShadow:
              "0 20px 24px -4px rgba(0,0,0,0.08), 0 8px 8px -4px rgba(0,0,0,0.04)",
          }}
        >
          {sideNavItems.map(({ key, label, icon, iconGrey }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveNav(key)}
              className={`flex items-center gap-2 px-3 py-2 h-10 rounded-[8px] border text-[14px] font-medium text-left transition ${
                activeNav === key
                  ? "bg-[#F7E6E6] border-[#DE9999] text-[#AD0101]"
                  : "border-transparent text-[#6A7282] hover:bg-[#F7F8FA]"
              }`}
            >
              <img
                src={activeNav === key ? icon : iconGrey}
                alt=""
                className="h-[18px] w-[18px] shrink-0 object-contain"
              />
              {label}
            </button>
          ))}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-[#AD0101] mb-4"
          >
            <img src={BackArrow} alt="Back" className="h-3.5 w-3.5 object-contain" />
            <span className="text-[12px] leading-5 font-semibold">Back</span>
          </button>
          {activeNav === "indicators" ? (
            <RoleInputIndicators
              roleId={roleId}
              accountId={accountId}
              onBack={() => setActiveNav("master")}
            />
          ) : (
          <>
          {/* Tabs */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-4 h-[60px] rounded-[12px] border border-[#BABABA] bg-white px-[9px] py-2">
              <button
                type="button"
                onClick={() => setMainTab("role-config")}
                className={`px-[15px] py-2.5 rounded-[8px] text-[14px] font-medium transition ${
                  mainTab === "role-config" ? "bg-[#FFF2F2] text-[#AD0101]" : "text-[#6A7282]"
                }`}
              >
                Role Configuration
              </button>
              <button
                type="button"
                onClick={() => setMainTab("attendance")}
                className={`px-[15px] py-2.5 rounded-[8px] text-[14px] font-medium transition ${
                  mainTab === "attendance" ? "bg-[#FFF2F2] text-[#AD0101]" : "text-[#6A7282]"
                }`}
              >
                Attendance Required
              </button>
            </div>
          </div>

          {mainTab === "attendance" ? (
            <RoleAttendanceSection roleId={roleId} accountId={accountId} />
          ) : (
          <div className="flex flex-col gap-6">
            <ConfigSection
              title="Designation"
              addLabel="Add Designation"
              searchPlaceholder="Search Desination"
              searchTerm={desigSearch}
              onSearch={setDesigSearch}
              onAdd={() => openModal("designation")}
              items={designations}
              onDelete={(item) => confirmDelete(item, "designation")}
              loading={desigLoading}
            />
            <ConfigSection
              title="Gender"
              addLabel="Add Gender"
              searchPlaceholder="Search Gender"
              searchTerm={genderSearch}
              onSearch={setGenderSearch}
              onAdd={() => openModal("gender")}
              items={genders}
              onDelete={(item) => confirmDelete(item, "gender")}
              loading={genderLoading}
            />
            <ConfigSection
              title="Employment Type"
              addLabel="Add Employment"
              searchPlaceholder="Search Employment"
              searchTerm={empSearch}
              onSearch={setEmpSearch}
              onAdd={() => openModal("employment")}
              items={employments}
              onDelete={(item) => confirmDelete(item, "employment")}
              loading={empLoading}
            />
            <RoleThresholds />
          </div>
          )}
          </>
          )}
        </main>
      </div>

      {/* Add modal */}
      <Modal
        open={modal.open}
        onCancel={closeModal}
        footer={null}
        centered
        title={
          modal.section === "designation"
            ? "Add Designation"
            : modal.section === "gender"
            ? "Add Gender"
            : "Add Employment Type"
        }
      >
        <Input
          value={modal.value}
          onChange={(e) => setModal((p) => ({ ...p, value: e.target.value }))}
          onPressEnter={handleAdd}
          autoFocus
          placeholder="Enter name"
          className="h-[42px]"
        />
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={closeModal}
            className="h-10 px-5 rounded border border-[#D9DDE3] bg-white text-[14px] font-medium text-[#1F2020]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            className={`h-10 px-5 rounded text-white text-[14px] font-medium transition ${
              saving ? "bg-[#E47777] cursor-not-allowed" : "bg-[#AD0101] hover:bg-[#8C0101]"
            }`}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={!!deleteTarget}
        onCancel={() => !deleting && setDeleteTarget(null)}
        footer={null}
        centered
        width={520}
      >
        <div className="flex flex-col items-center pt-2 pb-1">
          <div className="flex items-center gap-3">
            <ExclamationCircleFilled className="text-[22px] text-[#FAAD14]" />
            <span className="text-[20px] font-semibold leading-7 text-[#1F2020]">
              Are you sure you want to delete this?
            </span>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              className="h-[52px] rounded-[10px] border border-[#D9DDE3] bg-white px-[30px] text-[14px] font-medium text-[#6A7282] transition hover:bg-[#F7F8FA] disabled:opacity-60"
            >
              No
            </button>
            <button
              type="button"
              onClick={runDelete}
              disabled={deleting}
              className="h-[52px] rounded-[10px] border border-[#AD0101] bg-[#AD0101] px-[30px] text-[14px] font-medium text-white transition disabled:opacity-60"
            >
              {deleting ? "Deleting..." : "Yes"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RoleMasterConfiguration;
